/**
 * Tournament grouping for the ingested archive.
 *
 * SYSTEM-ingested competitions are one row per SEASON ("IPL 2019",
 * "BBl 2024", "LPL 2020/21"). The Matches admin can view them season by
 * season or as whole tournaments; the tournament view feeds the
 * tournament-wide Sync, which maps each player once across every season.
 *
 * Grouping key = the name with its season token removed, lowercased, and
 * punctuation (incl. underscores) folded to spaces. SEASON_RE runs AFTER
 * the underscore fold on purpose: in "CPL_2026_Ball_by_Ball" the year is
 * wrapped in word characters, so `\b` never fires until `_` is a space.
 *
 * Acronyms are deliberately NOT expanded the way the USER-scope list
 * does (owner_api_match_export.LEAGUE_ALIASES): in this archive "BPL" is
 * Bangladesh while Baroda is spelled out, "APL" is Andhra while Assam is
 * spelled out, "MPL"/"DPL" would be the wrong leagues. Only the explicit
 * pairs below are merged — each one checked against prod on 2026-09-28.
 */

const SEASON_RE = /\b(?:19|20)\d{2}(?:\s*[/-]\s*\d{2,4})?\b/g;
const YEAR_RE = /(?:19|20)\d{2}/;

const TOURNAMENT_ALIASES = {
  "chhattisgarh cricket premier league": "ccpl",
  "jharkhand premier league": "jpl",
  "cpl ball by ball ilt20format": "cpl",
};

function tournamentKey(name) {
  const k = String(name || "")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(SEASON_RE, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
  return TOURNAMENT_ALIASES[k] || k;
}

function stripSeason(name) {
  return String(name || "")
    .replace(/_/g, " ")
    .replace(SEASON_RE, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function seasonYear(name) {
  const m = String(name || "").match(YEAR_RE);
  return m ? Number(m[0]) : null;
}

/**
 * [{id, name, match_count}] (one per season) →
 * [{key, name, id, ids, seasons, match_count}] (one per tournament).
 *
 * `id` is the comma-joined season ids — the shape the match-list
 * endpoint already accepts for its `competition` filter.
 */
export function groupByTournament(competitions) {
  const groups = new Map();
  for (const c of competitions || []) {
    const key = tournamentKey(c.name);
    if (!key) continue;
    if (!groups.has(key)) groups.set(key, { key, seasons: [] });
    groups.get(key).seasons.push({
      id: c.id,
      name: c.name,
      year: seasonYear(c.name),
      match_count: c.match_count || 0,
    });
  }

  return [...groups.values()]
    .map((g) => {
      g.seasons.sort((a, b) => (a.year ?? 0) - (b.year ?? 0));
      // Display name: the spelling most seasons use ("BBL" beats "BBl"),
      // ties going to the longer, more descriptive one.
      const counts = new Map();
      for (const s of g.seasons) {
        const n = stripSeason(s.name);
        counts.set(n, (counts.get(n) || 0) + 1);
      }
      const name = [...counts.entries()].sort(
        (a, b) => b[1] - a[1] || b[0].length - a[0].length,
      )[0][0];
      const ids = g.seasons.map((s) => s.id);
      return {
        key: g.key,
        name,
        id: ids.join(","),
        ids,
        seasons: g.seasons,
        match_count: g.seasons.reduce((t, s) => t + s.match_count, 0),
      };
    })
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
}

export function seasonSpan(seasons) {
  const years = seasons.map((s) => s.year).filter(Boolean);
  if (!years.length) return "";
  const lo = Math.min(...years);
  const hi = Math.max(...years);
  return lo === hi ? String(lo) : `${lo}–${hi}`;
}
